import { useCallback, useState } from 'react'

type Result = {
  upload: (url: string, file: Blob, contentType?: string) => Promise<void>
  progress: number
  isUploading: boolean
  error?: string
}

export const useUploadProgress = (): Result => {
  const [progress, setProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const upload = useCallback((url: string, file: Blob, contentType?: string): Promise<void> => {
    setIsUploading(true)
    setError(undefined)
    return new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest()
      let settled = false
      const fail = (message: string): void => {
        if (settled) return
        settled = true
        setError(message)
        setProgress(0)
        setIsUploading(false)
        reject(new Error(message))
      }
      request.open('PUT', url, true)
      request.setRequestHeader('Content-Type', contentType ?? file.type)
      request.setRequestHeader('If-None-Match', '*')
      request.addEventListener('error', () => {
        fail('Upload error. Please try again later')
      })
      request.addEventListener('readystatechange', () => {
        if (request.readyState !== 4 || settled) return
        setIsUploading(false)
        if (request.status === 200) {
          settled = true
          setProgress(0)
          resolve()
        } else {
          fail('Invalid upload URL')
        }
      })
      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          setProgress(Math.round((event.loaded / event.total) * 100))
        }
      })
      request.send(file)
    })
  }, [])

  return { progress, isUploading, upload, error }
}
